import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MessageSquareWarning, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ChangeRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/change-requests");
      setRows(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function resolve(id) {
    try {
      await api.post(`/admin/change-requests/${id}/resolve`);
      toast.success("Marked as resolved");
      load();
    } catch {
      toast.error("Failed");
    }
  }

  return (
    <div data-testid="change-requests-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Inbox</div>
        <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Change Requests</h1>
        <p className="text-white/50 mt-2 text-sm">Updates requested by clients on their contracts.</p>
      </div>

      {loading && <div className="text-white/40 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="bg-[#0e0e0e] border border-white/10 p-10 text-center text-white/40 text-sm">
          No change requests yet.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <div
            key={r.id}
            data-testid={`change-request-${r.id}`}
            className={`bg-[#0e0e0e] border p-5 sm:p-6 ${r.resolved ? "border-white/5 opacity-60" : "border-amber-400/30"}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <MessageSquareWarning size={18} className={r.resolved ? "text-white/30" : "text-amber-300"} strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="text-white font-medium">{r.client_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link
                  to={`/admin/contracts/${r.contract_id}`}
                  className="inline-flex items-center gap-1.5 border border-white/15 text-white/80 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-1.5 text-xs"
                  data-testid={`open-contract-${r.id}`}
                >
                  Open Contract <ArrowRight size={12} />
                </Link>
                {!r.resolved && (
                  <button
                    onClick={() => resolve(r.id)}
                    data-testid={`resolve-${r.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#C9963F] text-black px-3 py-1.5 text-xs font-medium hover:bg-[#A67A2D]"
                  >
                    <CheckCircle2 size={12} /> Resolve
                  </button>
                )}
              </div>
            </div>
            <p className="text-white/80 mt-4 whitespace-pre-wrap text-sm leading-relaxed">{r.message}</p>
            {r.requester_name && (
              <div className="text-[10px] uppercase tracking-wider text-white/40 mt-3">— {r.requester_name}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
